/** The most a host can log for one booking, in cents; matches `logBookingSchema`. */
export const BOOKING_MAX_CENTS = 10_000_000;

export type ParsedBooking =
  | { ok: true; cents: number }
  | { ok: false; reason: 'empty' | 'negative' | 'zero' | 'invalid' | 'too-large' };

/**
 * Reads a dollar amount the host typed. Accepts "$1,200.50", "840", " 12.5 ".
 * A minus sign is refused rather than stripped: a negative booking is never a positive one.
 */
export function parseBookingCents(raw: string): ParsedBooking {
  const text = raw.trim();
  if (!text) return { ok: false, reason: 'empty' };
  const cleaned = text.replace(/[$,\s]/g, '');
  if (/^-/.test(cleaned) || /^\$?-/.test(text)) return { ok: false, reason: 'negative' };
  if (!/^\d+(\.\d{0,2})?$|^\.\d{1,2}$/.test(cleaned)) return { ok: false, reason: 'invalid' };
  const cents = Math.round(Number(cleaned) * 100);
  if (!Number.isFinite(cents)) return { ok: false, reason: 'invalid' };
  if (cents === 0) return { ok: false, reason: 'zero' };
  if (cents > BOOKING_MAX_CENTS) return { ok: false, reason: 'too-large' };
  return { ok: true, cents };
}

/** What to say under the field, or null when there is nothing to correct yet. */
export function bookingValueError(parsed: ParsedBooking): string | null {
  if (parsed.ok || parsed.reason === 'empty') return null;
  if (parsed.reason === 'too-large') return 'Enter an amount up to $100,000.';
  return 'Enter an amount above $0, like 840. Negative amounts and refunds are not bookings.';
}
